import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useResizeDetector } from 'react-resize-detector';
import { Box } from '@material-ui/core';
import { match } from 'react-router';
import { useHistory } from 'react-router-dom';
import InteractivePropertyList from '../../components/InteractivePropertyList';
import { BAMWSO2ClientID, BAMWSO2SecretID } from '../../config';
import { RouteParams } from '../../routes/interactive.routes';
import { Property } from '../../types';
import * as mapUtil from '../../utilities/map/map';
import { getFeatureImageForProperty } from '../../services/interactive/data-service';
import MapCenter from '../../map/MapControls/MapCenter';
import MapStyles from '../../map/MapControls/MapStyles';
import {
  BAM_MAP_STYLES,
  BAM_MAP_STYLE_LIST,
  BAM_MAP_STYLE_SOURCES,
  preferredMapStyleKey,
} from '../../map/constants/MapStyles';
import { isEmptyObjectFromStr } from '../../utilities';
import InteractiveSurveyContext from '../../hooks/InteractiveSurveyContext';
import { filterPropertiesByFieldRules } from '../../utilities/property/propertyUtil';
import { addressKeys } from '../../data/constants';

/**
 * Map Style Persistence Architecture:
 * 
 * 1. User selects a map style in MapStyles component
 * 2. onMapStyleChange() saves the selection to localStorage under "preferredMapStyle"
 * 3. When properties are loaded, updateMapPoints() is called with an onReady callback
 * 4. onReady callback fires after map points load and the style engine is initialized
 * 5. applyMapStyle() is called via the callback to set the stored/selected theme
 * 
 * Priority order for style selection:
 * - User's saved preference (localStorage) - persists across all surveys
 * - Survey-specific configured style (if set)
 * - Default to CBRE Light theme
 * 
 * Flow: User Selection → localStorage → Load Survey → Apply Stored Style
 */

const drawerWidth = 504;
const drawerSMWidth = 364;
const mediumPlaceholderImg = '/assets/images/image-placeholder-medium.png';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      height: '100%',
    },
    pageTitle: {
      fontSize: 28,
      lineHeight: 1,
      color: theme.palette.primary.main,
      marginBottom: 12,
      marginTop: -10,
    },
    paper: {
      margin: 'auto',
      minWidth: 450,
      marginBottom: 16,
      height: 145,
      [theme.breakpoints.between('sm', 'md')]: {
        height: 145,
        minWidth: 320,
      },
    },
    drawer: {
      zIndex: 1,
      width: drawerWidth,
      paddingTop: '48px',
      flexShrink: 0,
      whiteSpace: 'nowrap',
      [theme.breakpoints.between('sm', 'md')]: {
        width: drawerSMWidth,
      },
    },
    drawerOpen: {
      width: drawerWidth,
      paddingTop: '48px',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      [theme.breakpoints.between('sm', 'md')]: {
        width: drawerSMWidth,
      },
      background: theme.palette.grey['50'],
    },
    drawerClose: {
      paddingTop: '48px',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: 'hidden',
      width: '48px !important',
      overflowY: 'hidden',
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9) + 1,
      },
      background: theme.palette.grey['50'],
    },
    content: {
      flexGrow: 1,
      height: '100%',
      width: 720,
      position: 'relative',
      [theme.breakpoints.between('sm', 'md')]: {
        width: 360,
      },
    },
    '@media (max-width: 767px)': {
      height: '94.5vh',
      width: '100vw',
    },
    '@media (max-width: 376px)': {
      height: 'calc(100vh - 6.5vh)',
      width: '100vw',
    },
    mapComp: {
      height: 'calc(100vh - 248px)',
      display: 'block',
    },
    propertyBox: {
      width: '100%',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    actionBar: {
      marginTop: 8,
      marginBottom: 12,
    },
    hideText: {
      marginLeft: 16,
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
    },
    arrowBtn: {
      padding: 6,
      marginBottom: '2px',
      color: theme.palette.text.primary,
    },
    hideMobileView: {
      display: 'none',
      '@media (min-width: 767px)': {
        display: 'block',
      },
    },
    '@global': {
      '.tooltip-box': {
        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
      },
      '.tooltip-img': {
        height: '140px',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
      },
      '.tooltip-address1': {
        fontSize: '1rem',
        lineHeight: 1.25,
        color: theme.palette.primary.dark,
        textAlign: 'left',
        margin: '10px 20px 0px 20px',
      },
      '.tooltip-address2': {
        fontSize: '12px',
        color: theme.palette.grey[800],
        textAlign: 'left',
        padding: '0px 20px 15px 20px',
      },
      '.action-list': {
        padding: '0px 20px 20px 20px',
      },
      '.tooltip-action-view': {
        background: theme.palette.primary.main,
        borderRadius: '2px',
        width: '100%',
        height: '30px',
        color: 'white',
        cursor: 'pointer',
        fontSize: '1rem',
      },
      '.tooltip-action-view:disabled': {
        opacity: 0.8,
        background: '#E3E3E3',
        color: '#868686',
      },
      starIcon: {
        backgroundColor: '#F9DD4B',
      },
    },
  }),
);

const InteractiveSurveyMarketMap: React.FC<Props> = ({ match: { params } }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { surveyId } = params as RouteParams;
  const { updateMapPoints, getIconData, getActiveIconColor } = mapUtil.default;
  const [open, setOpen] = React.useState(true);
  const [openPopup, setOpenPopup] = React.useState(0);
  const [properties, setProperties] = React.useState<Property[]>([]);
  const { height, width, ref } = useResizeDetector();
  const { survey } = useContext(InteractiveSurveyContext);
  const mapCompPrv = useRef<any>();
  
  // Store survey, getIconData, and getActiveIconColor in refs to access latest values
  // without creating dependency chain that causes toggleMarkers to recreate on every survey change
  const surveyRef = useRef(survey);
  const getIconDataRef = useRef(getIconData);
  const getActiveIconColorRef = useRef(getActiveIconColor);

  // Update refs whenever these values change
  React.useEffect(() => {
    surveyRef.current = survey;
    getIconDataRef.current = getIconData;
    getActiveIconColorRef.current = getActiveIconColor;
  }, [survey, getIconData, getActiveIconColor]);

  // Store the resolved style in state using a lazy initializer so it only runs once on mount.
  // Priority: localStorage preference > survey config > CBRE Light default.
  const [currentMapStyle, setCurrentMapStyle] = React.useState(() => {
    const storedStyle = localStorage.getItem(preferredMapStyleKey);
    if (storedStyle && BAM_MAP_STYLE_LIST.includes(storedStyle)) {
      return storedStyle;
    }
    if (survey?.marketMapStyle && BAM_MAP_STYLE_LIST.includes(survey.marketMapStyle)) {
      return survey.marketMapStyle;
    }
    return BAM_MAP_STYLES.cbreLight;
  });

  // Sync the state whenever survey loads or changes.
  // Priority: user's saved preference (localStorage) > survey-specific style > default
  // Only apply survey style if user hasn't saved a preference yet.
  React.useEffect(() => {
    const storedStyle = localStorage.getItem(preferredMapStyleKey);
    if (!storedStyle || !BAM_MAP_STYLE_LIST.includes(storedStyle)) {
      // No valid user preference stored; use survey config or default
      if (survey?.marketMapStyle && BAM_MAP_STYLE_LIST.includes(survey.marketMapStyle)) {
        setCurrentMapStyle(survey.marketMapStyle);
      } else {
        setCurrentMapStyle(BAM_MAP_STYLES.cbreLight);
      }
    }
    // If storedStyle exists and is valid, keep it (don't override with survey config)
  }, [survey?.marketMapStyle, survey?.id]);

  /**
   * Memoize the applyMapStyle function to maintain stable reference across renders.
   * This is safe because it only depends on stable references (mapCompPrv ref, BAM_MAP_STYLE_SOURCES constant).
   * A stable reference prevents unnecessary re-renders of components receiving this function as a prop
   * and allows it to be safely used in dependency arrays of useCallback/useMemo.
   */
  const applyMapStyle = React.useCallback((style: string) => {
    // Apply the selected map style to the map component.
    // updateMapStyles() is a web component method that changes the map's visual theme.
    if (mapCompPrv?.current?.updateMapStyles) {
      mapCompPrv.current.updateMapStyles(
        BAM_MAP_STYLE_SOURCES[style] || BAM_MAP_STYLE_SOURCES.cbreLight,
      );
    }
  }, []);

  /**
   * Opens the side drawer.
   * Memoized to maintain stable reference for event handlers.
   */
  const handleDrawerOpen = React.useCallback(() => {
    setOpen(true);
  }, []);

  /**
   * Closes the side drawer.
   * Memoized to maintain stable reference for event handlers.
   */
  const handleDrawerClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  /**
   * Resets the map view when mapDataStr indicates a map reset.
   * Memoized because it's used in event handlers and needs a stable reference.
   */
  const resetMap = React.useCallback((mapDataStr: string | undefined) => {
    if (
      mapDataStr &&
      typeof JSON.parse(mapDataStr) === 'object' &&
      !isEmptyObjectFromStr(mapDataStr)
    ) {
      const mapData = JSON.parse(mapDataStr);
      if (mapData.mapReset) {
        mapCompPrv.current.zoomToFit();
      }
    }
  }, []);

  /**
   * Removes the active popup from the map and resets icon colors.
   * Memoized with useCallback. Uses refs to access latest survey and getIconData
   * without creating a dependency chain that causes unnecessary re-renders.
   * Dependencies: none (uses refs to access latest values)
   */
  const removePopup = React.useCallback(() => {
    const ele = mapCompPrv.current.querySelector('.custom-popover');
    if (ele) {
      ele.parentElement.removeChild(ele);
    }
    setOpenPopup(0);
    mapCompPrv.current.getPoints().then((points: any) => {
      /* eslint no-param-reassign: "error" */
      points.features.map((prop: any) => {
        if (prop.properties.markerConfig.iconColor) {
          const property: Property[] = surveyRef.current.properties.filter(
            (item) => item.id === prop.id);
          prop.properties.markerConfig.iconColor = getIconDataRef.current(property[0]).iconColor;
        }
        return null;
      });
      points.features.map((_: any, index: number) => mapCompPrv.current.updatePoint(points.features[index]));
    });
  }, []);

  /**
   * Toggles marker selection and displays/hides popup for property details.
   * Updates marker color to highlight the active property.
   * Memoized with useCallback. Uses refs to access latest values without dependencies.
   * This prevents unnecessary re-creation when survey or utilities change, breaking the circular dependency chain.
   * Dependencies: removePopup
   */
  const toggleMarkers = React.useCallback(async (pinNum: number) => {
    const map = mapCompPrv.current;
    removePopup();
    const points = Promise.resolve(map.getPoints());
    const popupNumber = pinNum + 1;

    // eslint-disable-next-line no-shadow
    await points.then((point) => {
      /* eslint no-param-reassign: "error" */
      point.features.map((_: any, index: number) => mapCompPrv.current.updatePoint(point.features[index]));
      const property: Property[] = surveyRef.current.properties.filter(
        (item) => item.id === point.features[pinNum].id);
      point.features[pinNum].properties.markerConfig.iconColor = getActiveIconColorRef.current(property[0]);
      map.updatePoint(point.features[pinNum]);
      setOpenPopup(point.features[pinNum].properties.id);
      map.togglePopup(popupNumber);
    });
  }, [removePopup]);

  useEffect(() => {
    if (mapCompPrv?.current) {
      mapCompPrv.current.mapResize(width, height);
      if (properties.length > 0) {
        resetMap('{"mapReset":true}');
      }
    }
  }, [width, height, properties, resetMap]);

  useEffect(() => {
    let preventMultiTriggerTimer: any = null;
    const pointClickedHandler = (point: any) => {
      clearTimeout(preventMultiTriggerTimer);
      preventMultiTriggerTimer = setTimeout(() => {
        toggleMarkers(point.detail.value.properties.id - 1);
      }, 10);
    };

    const actionFired = (event: any) => {
      const {
        value: { action, id },
      } = event.detail;
      if (action && id) {
        history.push(`/${surveyId}/property/${id}`);
      }
    };

    if (mapCompPrv?.current) {
      mapCompPrv.current.addEventListener('hostActionTriggered', actionFired);
      mapCompPrv.current.addEventListener('pointClicked', pointClickedHandler);
    }
    return () => {
      mapCompPrv?.current?.removeEventListener(
        'hostActionTriggered',
        actionFired,
      );
      mapCompPrv?.current?.removeEventListener('pointClicked', pointClickedHandler);
    };
  }, [toggleMarkers, history, surveyId]);

  const setPropertiesOnMap = () => {
    let propertyList = filterPropertiesByFieldRules(
      survey.properties,
      survey.surveyPropertyFieldRule,
      addressKeys,
    );
    const surveyShortlisted = localStorage.getItem('shortlisted') === 'true';
    propertyList = propertyList.filter(
      (item: Property) =>
        (!surveyShortlisted && !item.isHidden) ||
        (surveyShortlisted && item.metaData?.isClientShortlist),
    );
    const apiCalls = propertyList.map((item: Property) =>
      getFeatureImageForProperty(item.ownerId, item.id, 'IMAGES'),
    );

    Promise.all(apiCalls)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          propertyList = propertyList.map((item: Property) => {
            const imgObj = data.find((img: any) => img?.propertyId === item.id);
            const imgUrl = imgObj?.cloudfrontUrl || mediumPlaceholderImg;
            return {
              ...item,
              imgUrl,
            };
          }) as Property[];
        }
      })
      // eslint-disable-next-line no-console
      .catch((err) => console.error('Error fetching property images:', err))
      .finally(() => {
        setProperties(propertyList);
        // Pass the callback to updateMapPoints that will apply the stored/selected map style
        // after points are loaded and the map is ready. This ensures the theme loads cleanly
        // without flicker, since the map engine needs time to initialize after adding points.
        // 
        // Read localStorage directly inside the callback to ensure we use the latest user preference,
        // not the stale value captured at callback creation time. This handles the race condition
        // where currentMapStyle might change between callback registration and execution (500ms delay).
        updateMapPoints(
          propertyList,
          mapCompPrv,
          survey,
          '{"mapReset":true}',
          undefined,
          undefined,
          () => {
            const latestStyle = localStorage.getItem(preferredMapStyleKey);
            applyMapStyle(
              latestStyle && BAM_MAP_STYLE_LIST.includes(latestStyle)
                ? latestStyle
                : currentMapStyle
            );
          },
        );
        if (mapCompPrv?.current) {
          mapCompPrv.current.enableZoomControls(true);
        }
      });
  };
  useEffect(() => {
    setPropertiesOnMap();
  }, [survey, i18n.language]);

  /**
   * Recenters and zooms to fit all markers on the map.
   * Memoized to maintain stable reference for child components.
   */
  const onRecenterChange = React.useCallback(() => {
    if (mapCompPrv?.current) {
      mapCompPrv.current.zoomToFit();
    }
  }, []);

  // Memoize callback to prevent recreating function on every render.
  // This prevents MapStyles from re-rendering due to new function reference.
  const onMapStyleChange = React.useCallback((style: string) => {
    // Save user's style selection to localStorage so it persists across page navigation
    localStorage.setItem(preferredMapStyleKey, style);
    // Update state so the radio button selection stays in sync without reading localStorage again
    setCurrentMapStyle(style);
    // Immediately apply the selected style to the map
    applyMapStyle(style);
  }, [applyMapStyle]);

  return (
    <>
      {survey?.id && (
        <div className={classes.root}>
          <main className={classes.content} ref={ref}>
            <mapping-component
              vendor="mapbox"
              client-id={BAMWSO2ClientID}
              secret-id={BAMWSO2SecretID}
              width={width && width > 676 ? width : '100vw'}
              height={width && width > 676 ? height : '100vh'}
              ref={mapCompPrv}
              class={classes.mapComp}
              onClick={removePopup}
            >
              <MapCenter onRecenterChange={onRecenterChange} />
              <MapStyles
              onMapStyleChange={onMapStyleChange}
              survey={survey}
              currentStyle={currentMapStyle}
              />
            </mapping-component>
          </main>
          <Drawer
            variant="permanent"
            anchor="right"
            className={clsx(classes.drawer, classes.hideMobileView, {
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            })}
            classes={{
              paper: clsx({
                [classes.drawerOpen]: open,
                [classes.drawerClose]: !open,
              }),
            }}
          >
            <div className={classes.actionBar}>
              {open ? (
                <div>
                  <span className={classes.hideText}>{t('nav.hide')}</span>
                  <IconButton
                    onClick={handleDrawerClose}
                    className={classes.arrowBtn}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </div>
              ) : (
                <IconButton
                  onClick={handleDrawerOpen}
                  className={classes.arrowBtn}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}
            </div>
            <Box
              className={classes.propertyBox}
              style={{ visibility: open ? 'visible' : 'hidden' }}
            >
              <InteractivePropertyList
                propertyList={properties}
                shortListPins={setPropertiesOnMap}
                cardClicked={(pin: number) => toggleMarkers(pin)}
                cardSelected={openPopup}
              />
            </Box>
          </Drawer>
        </div>
      )}
    </>
  );
};

type Props = {
  match: match;
};

InteractiveSurveyMarketMap.displayName = 'InteractiveSurveyMarketMap';
export default InteractiveSurveyMarketMap;
